import { z } from "zod";

import { baseEntityFields } from "./base.js";

/**
 * Types primitifs supportés par un champ personnalisé (customField).
 * Convention modélisée dès maintenant (cf. bible v2) : chaque org peut définir
 * des champs additionnels sur ses entités sans forker le cœur.
 */
export const customFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "number",
  "boolean",
  "date",
  "select",
  "multiselect",
  "email",
  "phone",
  "url",
]);

/** Type d'un champ personnalisé. */
export type CustomFieldType = z.infer<typeof customFieldTypeSchema>;

/**
 * Option d'un champ de type `select` / `multiselect`.
 * - `value` : valeur stockée (stable, machine).
 * - `label` : libellé affiché (humain).
 */
export const customFieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

/** Type d'une option de champ select. */
export type CustomFieldOption = z.infer<typeof customFieldOptionSchema>;

/**
 * Définition d'un champ personnalisé (`customFieldDefinition`).
 *
 * Décrit UN champ custom rattaché à une entité (`entity`) pour une org donnée.
 * Étend les champs standard car une définition est elle-même un document Firestore.
 *
 * - `orgId`    : org propriétaire de la définition.
 * - `entity`   : nom de l'entité cible (ex. "contact").
 * - `key`      : clé technique du champ (stable, stockée sur les documents cibles).
 * - `label`    : libellé affiché.
 * - `type`     : type primitif du champ.
 * - `required` : champ obligatoire à la saisie.
 * - `options`  : options pour les types select/multiselect.
 * - `order`    : ordre d'affichage dans les formulaires.
 * - `helpText` : texte d'aide optionnel.
 */
export const customFieldDefinitionSchema = z.object({
  ...baseEntityFields,
  orgId: z.string(),
  entity: z.string().min(1),
  key: z
    .string()
    .min(1)
    .regex(
      /^[a-z][a-zA-Z0-9_]*$/,
      "key doit commencer par une minuscule et ne contenir que [a-zA-Z0-9_]",
    ),
  label: z.string().min(1),
  type: customFieldTypeSchema,
  required: z.boolean().default(false),
  options: z.array(customFieldOptionSchema).optional(),
  order: z.number().int().nonnegative().default(0),
  helpText: z.string().optional(),
});

/** Type d'une définition de champ personnalisé. */
export type CustomFieldDefinition = z.infer<typeof customFieldDefinitionSchema>;
